// VoiceChannelComponents.js
/**
 * Reusable components for Voice Channel configuration
 */

import React, { useState, useEffect } from 'react';
import { PlayIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * VoiceSelector Component
 * Allows selecting an ElevenLabs voice with preview functionality
 */
export const VoiceSelector = ({ value, onChange, disabled }) => {
  const [previewLoading, setPreviewLoading] = useState(null);

  // ElevenLabs voice options
  const voiceOptions = [
    { id: 'rachel', name: 'Rachel - Professional Female', gender: 'female', accent: 'American' },
    { id: 'clyde', name: 'Clyde - Professional Male', gender: 'male', accent: 'American' },
    { id: 'domi', name: 'Domi - Friendly Female', gender: 'female', accent: 'American' },
    { id: 'dave', name: 'Dave - Conversational Male', gender: 'male', accent: 'British' },
    { id: 'fin', name: 'Fin - Young Male', gender: 'male', accent: 'Irish' },
    { id: 'bella', name: 'Bella - Soft Female', gender: 'female', accent: 'American' },
    { id: 'antoni', name: 'Antoni - Warm Male', gender: 'male', accent: 'American' },
    { id: 'elli', name: 'Elli - Energetic Female', gender: 'female', accent: 'American' },
    { id: 'josh', name: 'Josh - Friendly Male', gender: 'male', accent: 'American' },
    { id: 'arnold', name: 'Arnold - Mature Male', gender: 'male', accent: 'American' },
    { id: 'adam', name: 'Adam - Deep Male', gender: 'male', accent: 'American' },
    { id: 'sam', name: 'Sam - Young Male', gender: 'male', accent: 'American' }
  ];

  const handlePreview = async (voiceId) => {
    setPreviewLoading(voiceId);

    // TODO: Implement actual preview with ElevenLabs API
    // For now, just simulate a preview
    setTimeout(() => {
      setPreviewLoading(null);
    }, 1500);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto pr-2">
        {voiceOptions.map((voice) => (
          <div
            key={voice.id}
            onClick={() => !disabled && onChange(voice.id)}
            className={`relative flex items-center justify-between p-3 border rounded-lg text-left transition-colors ${
              value === voice.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {voice.name}
                </span>
                {value === voice.id && (
                  <CheckIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                )}
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="capitalize">{voice.gender}</span>
                <span>•</span>
                <span>{voice.accent}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePreview(voice.id);
              }}
              disabled={previewLoading === voice.id || disabled}
              className="ml-3 p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
              title="Preview voice"
            >
              {previewLoading === voice.id ? (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              ) : (
                <PlayIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Click the play button to preview each voice
      </p>
    </div>
  );
};

/**
 * PhoneNumberMultiSelect Component
 * Allows selecting multiple phone numbers (DIDs) with checkbox interface
 */
export const PhoneNumberMultiSelect = ({
  selectedNumbers = [],
  onChange,
  availableNumbers = [],
  loading = false,
  disabled = false,
  currentAgentId = null // ID of the agent being configured
}) => {
  const handleToggle = (numberId) => {
    if (disabled) return;

    if (selectedNumbers.includes(numberId)) {
      onChange(selectedNumbers.filter(id => id !== numberId));
    } else {
      onChange([...selectedNumbers, numberId]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;

    if (selectedNumbers.length === availableNumbers.length) {
      onChange([]);
    } else {
      onChange(availableNumbers.map(num => num._id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (availableNumbers.length === 0) {
    return (
      <div className="text-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No phone numbers (DIDs) available.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Purchase phone numbers in Voice &gt; Phone Numbers (DIDs) first.
        </p>
      </div>
    );
  }

  const unassignedNumbers = availableNumbers.filter(num =>
    !num.assignment || num.assignment.type === 'pool' || num.assignment.type === 'unassigned'
  );

  return (
    <div className="space-y-3">
      {/* Select All */}
      <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          Select phone numbers to assign to this agent
        </span>
        <button
          type="button"
          onClick={handleSelectAll}
          disabled={disabled || unassignedNumbers.length === 0}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selectedNumbers.length === unassignedNumbers.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Phone Number List */}
      <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
        {availableNumbers.map((number) => {
          // Check if number is assigned to a DIFFERENT agent (not this one)
          const assignedAgentId = number.assignment?.agentId?._id || number.assignment?.agentId;
          const isAssignedToOther = number.assignment &&
                                     number.assignment.type === 'agent' &&
                                     assignedAgentId &&
                                     assignedAgentId.toString() !== currentAgentId?.toString();
          const isDisabled = disabled || isAssignedToOther;

          return (
            <label
              key={number._id}
              className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                selectedNumbers.includes(number._id)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              } ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedNumbers.includes(number._id)}
                onChange={() => handleToggle(number._id)}
                disabled={isDisabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {number.phoneNumber}
                  </span>
                  {number.friendlyName && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({number.friendlyName})
                    </span>
                  )}
                </div>

                {isAssignedToOther && (
                  <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Assigned to: {number.assignment?.agentId?.agentName || 'Another agent'}
                  </div>
                )}

                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="capitalize">{number.numberType || 'local'}</span>
                  {number.features?.callRecording?.enabled && (
                    <>
                      <span>•</span>
                      <span>Recording</span>
                    </>
                  )}
                  {number.status && (
                    <>
                      <span>•</span>
                      <span className="capitalize">{number.status}</span>
                    </>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Summary */}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {selectedNumbers.length} number{selectedNumbers.length !== 1 ? 's' : ''} selected
          {unassignedNumbers.length < availableNumbers.length && (
            <span className="ml-2">
              • {availableNumbers.length - unassignedNumbers.length} assigned to other agents
            </span>
          )}
        </p>
      </div>
    </div>
  );
};
